const ADM2_SRC = `${process.env.PUBLIC_URL || ''}/maps/TR-adm2-compressed.svg`;
const SVG_NS = 'http://www.w3.org/2000/svg';

let adm2MarkupCache = null;

/**
 * TR-01 → "01" (TR-adm2compressed içinde ilçe id öneki: 01-aladağ)
 * @param {string | null | undefined} iso2
 * @returns {string | null}
 */
export function platePrefixFromIso2(iso2) {
  if (!iso2 || typeof iso2 !== 'string') return null;
  const m = iso2.trim().match(/^TR-(\d{1,2})$/i);
  if (!m) return null;
  return String(parseInt(m[1], 10)).padStart(2, '0');
}

export async function fetchAdm2Markup() {
  if (adm2MarkupCache) return adm2MarkupCache;
  const res = await fetch(ADM2_SRC);
  if (!res.ok) throw new Error(`İlçe haritası yüklenemedi (${res.status})`);
  adm2MarkupCache = await res.text();
  return adm2MarkupCache;
}

/** Test / bellek temizliği */
export function clearAdm2MarkupCache() {
  adm2MarkupCache = null;
}

/**
 * Tam adm2 SVG metninden yalnız plakaya ait ilçe gruplarıyla yeni bir SVG üretir; viewBox ilçelere sığdırılır.
 * @param {string} adm2Markup
 * @param {string} platePrefix — "01", "34" …
 * @returns {SVGSVGElement | null}
 */
export function createDistrictSliceSvg(adm2Markup, platePrefix) {
  if (!adm2Markup || !platePrefix) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(adm2Markup, 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;

  const srcSvg = doc.querySelector('svg');
  const srcFeatures = doc.querySelector('#features');
  if (!srcSvg || !srcFeatures) return null;

  const prefix = `${platePrefix}-`;
  const children = Array.from(srcFeatures.children).filter(
    (el) => el.tagName === 'g' && el.getAttribute('id')?.startsWith(prefix)
  );
  if (!children.length) return null;

  const outSvg = document.createElementNS(SVG_NS, 'svg');
  outSvg.setAttribute('xmlns', SVG_NS);
  outSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  outSvg.setAttribute('version', '1.1');
  outSvg.setAttribute('xml:space', 'preserve');

  const srcDefs = srcSvg.querySelector('defs');
  if (srcDefs) {
    outSvg.appendChild(srcDefs.cloneNode(true));
  }

  const outFeatures = document.createElementNS(SVG_NS, 'g');
  outFeatures.setAttribute('id', 'features');
  outFeatures.setAttribute('fill', 'silver');
  outFeatures.setAttribute('stroke-linecap', 'round');
  outFeatures.setAttribute('stroke-linejoin', 'round');

  children.forEach((ch) => outFeatures.appendChild(ch.cloneNode(true)));
  outSvg.appendChild(outFeatures);

  /** İlk viewBox (yalnız poligon); etiketler sonrası {@link fitDistrictSliceViewBox} ile genişletilir */
  const vb = measureFeaturesViewBox(outSvg);
  if (vb) {
    outSvg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.width} ${vb.height}`);
  } else {
    const fallback = srcSvg.getAttribute('viewBox') || '1 0 1436 629';
    outSvg.setAttribute('viewBox', fallback);
  }

  return outSvg;
}

/**
 * Poligon viewBox’ı ilçe adları ve çizgi kalınlığından dar kalınca içerik kırpılabiliyor.
 * `addDistrictLabels` / `addProvinceLabels` sonrası çağrılmalı: #features ile etiket gruplarının bbox birleşimi + pay.
 *
 * @param {SVGSVGElement} svg
 * @param {{ paddingRatio?: number, paddingMin?: number }} [opts]
 */
export function fitDistrictSliceViewBox(svg, opts = {}) {
  if (!svg || svg.tagName?.toLowerCase() !== 'svg') return;
  const paddingRatio = typeof opts.paddingRatio === 'number' && opts.paddingRatio > 0 ? opts.paddingRatio : 0.07;
  const paddingMin = typeof opts.paddingMin === 'number' && opts.paddingMin > 0 ? opts.paddingMin : 16;

  const layers = ['#features', '#district-labels', '#province-labels'];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const sel of layers) {
    const el = svg.querySelector(sel);
    if (!el) continue;
    let b;
    try {
      b = el.getBBox();
    } catch {
      continue;
    }
    if (!b || b.width < 0.25 || b.height < 0.25) continue;
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return;

  const gw = Math.max(maxX - minX, 1e-6);
  const gh = Math.max(maxY - minY, 1e-6);
  let pad = Math.max(gw, gh) * paddingRatio;
  pad = Math.max(pad, paddingMin);

  const x = minX - pad;
  const y = minY - pad;
  const w = gw + 2 * pad;
  const h = gh + 2 * pad;

  svg.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
}

/**
 * @param {SVGSVGElement} svg — içinde #features olmalı
 */
function measureFeaturesViewBox(svg) {
  if (typeof document === 'undefined') return null;

  const holder = document.createElement('div');
  holder.setAttribute(
    'style',
    'position:fixed;left:-99999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;visibility:hidden'
  );
  document.body.appendChild(holder);

  svg.setAttribute('width', '1600');
  svg.setAttribute('height', '900');
  holder.appendChild(svg);

  const features = svg.querySelector('#features');
  let box = null;
  try {
    box = features?.getBBox();
  } catch {
    box = null;
  }

  holder.removeChild(svg);
  document.body.removeChild(holder);

  if (!box || box.width < 1 || box.height < 1) return null;

  const base = Math.max(box.width, box.height);
  let pad = base * 0.045;
  pad = Math.max(pad, 10);
  return {
    x: box.x - pad,
    y: box.y - pad,
    width: box.width + 2 * pad,
    height: box.height + 2 * pad,
  };
}
